import { Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import {
  METHODES_PAIEMENT,
  METHODE_PAIEMENT_LABELS,
  type MethodePaiement,
  type Paiement,
} from '@sirajoptic/shared';
import { BadgeReste } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { InputField, SelectField } from '@/components/ui/field';
import { useToast } from '@/components/ui/toast';
import { aujourdhui, date, dh } from '@/lib/format';
import { useAjouterPaiement, useSupprimerPaiement } from '../api/commandes.api';

/**
 * Grand livre des paiements d'une commande — règle 9.
 *
 * `avance` et `reste` sont recalculés par le serveur à chaque écriture ; rien
 * n'est stocké. Un reste négatif (trop-perçu) est affiché tel quel, ce n'est
 * pas une anomalie.
 */
export function PaiementsPanel({
  commandeId,
  paiements,
  total,
  avance,
  reste,
  lectureSeule,
}: {
  commandeId: string;
  paiements: Paiement[];
  total: number;
  avance: number;
  reste: number;
  lectureSeule?: boolean;
}) {
  const [ouvert, setOuvert] = useState(false);
  const [montant, setMontant] = useState('');
  const [datePaiement, setDatePaiement] = useState(aujourdhui());
  const [methode, setMethode] = useState<MethodePaiement>('ESPECES');
  const [erreur, setErreur] = useState<string | null>(null);

  const ajouter = useAjouterPaiement();
  const supprimer = useSupprimerPaiement();
  const toast = useToast();

  async function enregistrer() {
    const valeur = Number(montant.replace(',', '.'));
    if (!Number.isFinite(valeur) || valeur === 0) {
      setErreur('Saisissez un montant différent de zéro.');
      return;
    }
    try {
      await ajouter.mutateAsync({
        id: commandeId,
        input: { montant: valeur, date_paiement: datePaiement, methode, note: null },
      });
      toast.succes(`Paiement de ${dh(valeur)} enregistré.`);
      setMontant('');
      setErreur(null);
      setOuvert(false);
    } catch (e) {
      setErreur(e instanceof Error ? e.message : "Le paiement n'a pas pu être enregistré.");
    }
  }

  async function retirer(paiement: Paiement) {
    try {
      await supprimer.mutateAsync(paiement.id);
      toast.succes('Paiement supprimé.');
    } catch (e) {
      toast.erreur(e instanceof Error ? e.message : 'Suppression impossible.');
    }
  }

  return (
    <section>
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-ink">Paiements</h3>
        {!lectureSeule ? (
          <Button
            size="sm"
            variant="outline"
            icone={<Plus aria-hidden className="h-3.5 w-3.5" />}
            onClick={() => setOuvert((o) => !o)}
          >
            Ajouter un paiement
          </Button>
        ) : null}
      </div>

      <div className="grid grid-cols-3 gap-2 rounded-md border border-line bg-surface-sunken p-3 text-center">
        <div>
          <p className="text-xs uppercase tracking-wide text-ink-muted">Total</p>
          <p className="mt-0.5 font-semibold tabular">{dh(total)}</p>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wide text-ink-muted">Avance</p>
          <p className="mt-0.5 font-semibold tabular">{dh(avance)}</p>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wide text-ink-muted">Reste</p>
          <p className="mt-1 flex justify-center">
            <BadgeReste reste={reste} />
          </p>
        </div>
      </div>

      {ouvert ? (
        <div className="mt-3 grid grid-cols-1 items-end gap-2 rounded-md border border-mustard-300 bg-mustard-50 p-3 sm:grid-cols-4">
          <InputField
            label="Montant (DH)"
            type="number"
            step="0.01"
            value={montant}
            onChange={(e) => setMontant(e.target.value)}
            erreur={erreur ?? undefined}
            aide="Négatif pour un remboursement."
          />
          <InputField
            label="Date"
            type="date"
            value={datePaiement}
            onChange={(e) => setDatePaiement(e.target.value)}
          />
          <SelectField
            label="Méthode"
            value={methode}
            onChange={(e) => setMethode(e.target.value as MethodePaiement)}
          >
            {METHODES_PAIEMENT.map((m) => (
              <option key={m} value={m}>
                {METHODE_PAIEMENT_LABELS[m]}
              </option>
            ))}
          </SelectField>
          <Button variant="primary" onClick={() => void enregistrer()} chargement={ajouter.isPending}>
            Enregistrer
          </Button>
        </div>
      ) : null}

      {paiements.length === 0 ? (
        <p className="mt-3 rounded-md border border-dashed border-line px-3 py-4 text-center text-sm text-ink-muted">
          Aucun paiement enregistré.
        </p>
      ) : (
        <ul className="mt-3 divide-y divide-line rounded-md border border-line">
          {paiements.map((p) => (
            <li key={p.id} className="flex items-center gap-3 px-3 py-2">
              <span
                className={`w-24 shrink-0 text-sm font-semibold tabular ${
                  p.montant < 0 ? 'text-danger' : 'text-ink'
                }`}
              >
                {dh(p.montant, { signe: true })}
              </span>
              <span className="w-24 shrink-0 text-xs text-ink-muted">{date(p.date_paiement)}</span>
              <span className="min-w-0 flex-1 truncate text-xs text-ink-muted">
                {p.methode ? METHODE_PAIEMENT_LABELS[p.methode] : '—'}
                {p.note ? ` · ${p.note}` : ''}
              </span>
              {!lectureSeule ? (
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Supprimer ce paiement"
                  onClick={() => void retirer(p)}
                  disabled={supprimer.isPending}
                >
                  <Trash2 aria-hidden className="h-3.5 w-3.5 text-danger" />
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
